/**
 * EmptyState — for "no results yet" surfaces.
 *
 *   <EmptyState
 *     icon={<Receipt />}
 *     title="Chưa có hoá đơn nào"
 *     description="Tạo hoá đơn đầu tiên cho khách thuê hoặc nhập danh sách từ Excel."
 *     primaryAction={<Button iconLeft={<Plus />}>Tạo hoá đơn</Button>}
 *     secondaryAction={<Button kind="ghost">Nhập từ Excel</Button>}
 *   />
 *
 * Icon should be a Lucide icon (or any element). Pass `compact` for inline-in-table
 * empty states (smaller padding, no actions).
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Refresh } from './icons';

export function EmptyState({
  icon,
  title,
  description,
  primaryAction,
  secondaryAction,
  compact = false,
  className = '',
}) {
  return (
    <div className={[
      'flex flex-col items-center justify-center text-center',
      compact ? 'py-8 px-4' : 'py-16 px-6',
      className,
    ].join(' ')}>
      {icon && (
        <div className={[
          'inline-flex items-center justify-center text-stone-400 bg-stone-100 rounded-full mb-4',
          compact ? 'w-10 h-10' : 'w-14 h-14',
        ].join(' ')}>
          {React.isValidElement(icon) ? React.cloneElement(icon, { size: compact ? 18 : 22 }) : icon}
        </div>
      )}
      {title && (
        <h3 className={[
          'font-semibold text-stone-900 -tracking-tight',
          compact ? 'text-sm' : 'text-base',
        ].join(' ')}>
          {title}
        </h3>
      )}
      {description && (
        <p className={[
          'text-stone-500 max-w-md',
          compact ? 'text-xs mt-1' : 'text-sm mt-1.5',
        ].join(' ')}>
          {description}
        </p>
      )}
      {(primaryAction || secondaryAction) && (
        <div className="flex items-center gap-2 mt-5">
          {secondaryAction}
          {primaryAction}
        </div>
      )}
    </div>
  );
}

/**
 * QueryErrorBanner — shown inline when a react-query fails.
 *
 *   {isError && <QueryErrorBanner onRetry={refetch} />}
 */
export function QueryErrorBanner({
  message,
  onRetry,
  className = '',
}) {
  const { t } = useTranslation('common');
  return (
    <div className={[
      'flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700',
      className,
    ].join(' ')}>
      <Alert size={16} className="shrink-0 text-red-500" aria-hidden />
      <span className="flex-1">{message ?? t('empty.error.message')}</span>
      {onRetry && (
        <button
          onClick={onRetry}
          className="flex items-center gap-1 text-xs font-semibold text-red-600 hover:text-red-800 transition-colors"
        >
          <Refresh size={12} />
          {t('action.retry')}
        </button>
      )}
    </div>
  );
}
